import React, { ReactNode } from 'react';
import { Box, Link } from '@mui/material';
import {
	PageContainerBreadcrumbs,
	TextItem,
	TextItemLast,
} from 'shared/Pages/common/PageContainer/styles';
import { PAGE_MAX_WIDTH } from 'shared/_components/page/styles';
import { SizeType } from 'shared/Pages/common/PageContainer/types';

type TPageContainerProps = {
	parentTitle?: string;
	parentLink?: string;
	title?: string;
	actions?: ReactNode;
	size?: SizeType;
	children: ReactNode;
};

type FontSize = string | { xs: string; sm: string };

// Copy of Header font-sizes; the large (page title) size is 1.5rem, 2rem from `sm`.
const FONT_SIZE_MAP: Record<SizeType, FontSize> = {
	small: '1.375rem',
	medium: '1.5rem',
	large: { xs: '1.5rem', sm: '2rem' },
};

function getTitleLevel(size: SizeType) {
	switch (size) {
		case 'large':
			return 1;
		case 'medium':
			return 2;
		default:
			return 3;
	}
}

interface IPreviousItemProps {
	fontSize: FontSize;
	link?: string;
	children: ReactNode;
}

function PreviousItem({ fontSize, link, children }: IPreviousItemProps): JSX.Element {
	if (link) {
		return (
			<Link
				underline="hover"
				href={link}
				sx={{
					color: 'var(--lightText)',
				}}
			>
				{children}
			</Link>
		);
	}

	return (
		<TextItem color="var(--lightText)" sx={{ fontSize }}>
			{children}
		</TextItem>
	);
}

export function PageContainer(props: TPageContainerProps): JSX.Element {
	const { size = 'large', parentTitle, parentLink, title, actions, children } = props;

	const fontSize = FONT_SIZE_MAP[size];

	return (
		// Same width limit as `Page`; the scrollbar stays on the (full width) `main` element.
		<Box sx={{ maxWidth: PAGE_MAX_WIDTH }}>
			{/* Below 600px the actions wrap to a second row */}
			<Box sx={{ display: 'flex', width: '100%', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
				<PageContainerBreadcrumbs separator="»" sx={{ fontSize }}>
					{parentTitle && (
						<PreviousItem fontSize={fontSize} link={parentLink}>
							{parentTitle}
						</PreviousItem>
					)}
					<TextItemLast color="text.primary" sx={{ fontSize }} variant={`h${getTitleLevel(size)}`}>
						{title}
					</TextItemLast>
				</PageContainerBreadcrumbs>
				{!!actions && (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'flex-end',
							alignItems: 'center',
							flex: 'none',
							width: { xs: '100%', sm: 'auto' },
						}}
					>
						{actions}
					</Box>
				)}
			</Box>
			{children}
		</Box>
	);
}
